/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, timeout } from 'rxjs/operators';
import { MappingsService } from 'src/app/nav-processing/mappings/mappings.service';
import {
  MappingsOutput,
  MappingsOutputType,
} from 'src/app/nav-processing/mappings/mappings.model';
import { TouchNavigationService } from './touch-navigation/touch-navigation.service';
import { ProfileService } from '../profile/profile.service';
import { availableNavigationSources } from './navigation-cursor.model';
import { NavigationEnd, Router } from '@angular/router';
import { ScanNavigationService } from './scan-navigation/scan-navigation.service';
import {
  ScanEvent,
  ScanEventType,
} from './scan-navigation/scan-navigation.model';
import { NavProcessingService } from '../nav-processing/nav-processing.service';
import { CursorType } from '../manual-settings/manual-settings.model';
import { EyeNavigationService } from './eye-navigation.service';
import { GazeCursorService } from './gaze-cursor/gaze-cursor.service';
import { UserService } from '../user/user.service';
import { time } from 'console';
import { NavigablePage } from './navigable-page';

/**
 * Importante: O cursor procura por elementos que tenham o atributo data-grid-col aninhado do data-grid-row
 * o evento de click é injetado no elemento html que contém o data-grid-col.
 * Os elementos não devem interceptar diretamente os outros tipos de eventos relacionados a click e touch.
 */

interface Cursor {
  positionH: number;
  positionV: number;
  positionHV: number;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationCursorService {
  cursorElm = document.createElement('div');
  feedbackElm = document.createElement('div');

  currentItem: Element;
  row: Element;
  col = '';
  navegableItems: NodeListOf<Element>;
  navigableItems = [];
  navigableGroups: Array<Element[]> = [];
  resolutionHV = 6;

  currentGroupIndex = 0;
  currentElementIndex = 0;

  navCursorMovedBS = new BehaviorSubject<Element>(null);

  cursor: Cursor = { positionH: 0, positionV: 0, positionHV: null };

  digitalEventSub: Subscription;
  binaryEventSub: Subscription;
  // cancelEventSub: Subscription;
  feedbackEventSub: Subscription;

  //settings: NavigationCursorServiceSettings = {} as NavigationCursorServiceSettings;
  availableSources = availableNavigationSources;

  private cursorFiltered: Cursor = null;
  private cursorFilterTimer = null;

  private navGuardTimer = null;
  private navGuardTimerFinished = false;

  private highlightCursorItemsTimer = null;

  private navEnabled = true;
  private initialized = false;

  private scanNavStarted = false;
  private eyeTrackerNavStarted = false;

  private cursorLeftOffset = 0;
  private gazePauseToggleElm: Element | null = null;
  private cancelSubscription: Subscription;
  private activePage: NavigablePage | null = null;
  private activePageWithoutBack: boolean = false; //indica se a página atual não permite voltar

  private navigationStep: number = 0;
  private selectableItemsOnPage: [[number, number]]; // Lista de itens selecionáveis na página atual com seus índices

  constructor(  
    private mappings: MappingsService,
    private touchNavService: TouchNavigationService,
    private profile: ProfileService,
    private router: Router,
  private eyeTrackerNavService: EyeNavigationService,
  private gazeCursorService: GazeCursorService,
    private scanNavigationService: ScanNavigationService,
    protected user: UserService,
    private processing: NavProcessingService
  ) {
    // Creates the navigation cursor.
    // setTimeout(() => {
    //   // Confirmar se essa é a melhor forma de fazer isso.
    //   this.createNavCursorElm();
    // }, 10000);

    // this.createNavCursorElm();

    this.cancelSubscription = this.mappings.outputBS
      .pipe(filter((data) => data.type === MappingsOutputType.cancelSelection || data.type === MappingsOutputType.cancelSelectionAuto))
      .subscribe((data) => {
        console.log('Cancel event received', data, this.activePageWithoutBack);
        if (data.type === MappingsOutputType.cancelSelection) {
          if (this.activePage && this.navigationStep === 0) {
            this.activePage.onCancel();
          } else {
            this.onCancelSelectEvent();
          }
        } else if (this.activePageWithoutBack){
          if (this.activePage && this.navigationStep === 0) {
            this.activePage.onCancel();
          } else {
            this.onCancelSelectEvent();
          }
        } else{
          if (this.navigationStep > 0) {
            this.onCancelSelectEvent();
          }
        }
      });

    document.addEventListener(
      'click',
      (event) => {
        // Tries to get the navigable item from the click event.
        const navItem = this.touchNavService.searchForNavigableItem(
          event.target as Element
        );

        // Only click events generated by the navigation cursor is allowed to go ahead to navigable items.
        // In this case, pointerType is an empty string. On the other hand, if the click
        // is made by touch, the ponterType will be set accordingly (mouse or touch).
        if (navItem && (event as any).pointerType === 'touch') {
          event.stopImmediatePropagation();
        }
      },
      true
    );

    this.digitalEventSub = mappings.outputBS
      .pipe(filter((data) => this.filterEvents(data)))
      .pipe(
        filter(
          (data) =>
            data.type === MappingsOutputType.cursorPositionH ||
            data.type === MappingsOutputType.cursorPositionV ||
            data.type === MappingsOutputType.cursorPositionHV
        )
      )
      .subscribe((data) => {
        const ct = this.profile.profile.navSettingsBundle.cursorType;
        if (ct !== CursorType.EyeTracker && ct !== CursorType.GazeTracker) {
          this.onDigitalEvent(data);
        }
      });

    this.binaryEventSub = mappings.outputBS
      .pipe(filter((data) => this.filterEvents(data)))
      .pipe(filter((data) => data.type === MappingsOutputType.select))
      .pipe(filter((data) => this.guardSelectEvent(data)))
      .subscribe(() => {
        const ct = this.profile.profile.navSettingsBundle.cursorType;
        if (ct !== CursorType.EyeTracker && ct !== CursorType.GazeTracker) {
          this.onSelectEvent();
        }
      });

    // this.cancelEventSub = mappings.outputBS
    //   .pipe(filter((data) => this.filterEvents(data)))
    //   .pipe(filter((data) => data.type === MappingsOutputType.cancelSelection))
    //   .subscribe(() => {
    //     if (
    //       CursorType.EyeTracker !==
    //       this.profile.profile.navSettingsBundle.cursorType
    //     ) {
    //       this.onCancelSelectEvent();
    //     }
    //   });

    this.feedbackEventSub = mappings.outputBS
      .pipe(filter((data) => this.filterFeedbackEvents(data)))
      .pipe(filter((data) => data.type === MappingsOutputType.feedbackSelect))
      .pipe(distinctUntilChanged())
      .subscribe((data) => {
        const ct = this.profile.profile.navSettingsBundle.cursorType;
        if (ct !== CursorType.EyeTracker && ct !== CursorType.GazeTracker) {
          this.refreshFeedbackElm(data.value);
        }
      });

    setTimeout(() => {
      this.scanNavigationService.scanEvents
        .pipe(filter((data) => this.filterEvents(data)))
        .subscribe((ev) => {
          this.onScanEvent(ev);
        });
    }, 1);

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((ev) => {
        this.onUrlChanged(ev);
      });

    this.profile.navSettingsBundle$.subscribe((settings) => {
      //this.settings = settings;
      //this.touchNavService.settings = settings;
      //console.log('navigationCursorSettings', this.settings);

      if (this.navEnabled) {
        // console.log('[CURSOR] ADD feedback element');

        this.setupNavigationCursor();
        this.eyeTrackerNavService.createDwellElm();
      }

      // remove feedback element
      if (
        !this.profile.navSettingsBundle?.bundleOptionsState?.selectRawFeedback
      ) {
        // console.log('[CURSOR] Remove feedback element');

        this.refreshFeedbackElm(0);
        this.eyeTrackerNavService.removeDwellElm();
      }
    });

    // this.resume();
  }

  /**
   * Handles when the url changes.
   */
  onGridChanged(url: string, cursorLeftOffset = 0) {
    this.cursorLeftOffset = cursorLeftOffset;
    if (this.eyeTrackerNavStarted) {
      setTimeout(() => {
        this.eyeTrackerNavService.placeCursor();
      }, 500);
    }

    setTimeout(() => {
      this.setupNavigationCursor(url);
    }, 500);
  }

  /**
   * Pause the navigation.
   */
  pause() {
    this.navEnabled = false;
    this.removeNavCursorElm(); // Removes the cursor elm
    // switch(this.settings.navigationSource) {
    // case NavigationSource.scan: {
    //   console.log('peye tracker stop scan');
    //   this.scanNavigationService.stopScan();
    //  break;
    //}
    //case NavigationSource.eyeTracker: {
    //  console.log('peye tracker stop eyetracker');
    //  this.eyeTrackerNavService.stop();
    //  break;
    // }
    // default: {
    // if (this.scanNavStarted) {
    //   this.scanNavigationService.stopScan();
    //   this.scanNavStarted = false;
    // }
    // if(this.eyeTrackerNavStarted){
    //   //inibir clique do cursor
    //   this.eyeTrackerNavService.clickEnabled = false;
    // }
    //}
    //}
  }

  /**
   * Resumes the navigation.
   */
  resume() {
    this.navEnabled = true;
    this.setupNavigationCursor();
    //this.eyeTrackerNavService.clickEnabled = true;
  }

  registerGazePauseToggle(elm: Element | null) {
    this.gazePauseToggleElm = elm ?? null;
    this.gazeCursorService.setUserPauseToggleElement(this.gazePauseToggleElm);
  }

  toggleGazeCursorPause(elm?: Element | null): boolean {
    if (elm) {
      this.registerGazePauseToggle(elm);
    } else if (this.gazePauseToggleElm) {
      this.gazeCursorService.setUserPauseToggleElement(this.gazePauseToggleElm);
    }

    if (!this.gazePauseToggleElm) {
      return this.gazeCursorService.isUserPauseActive();
    }

    const cursorType = this.profile.profile?.navSettingsBundle?.cursorType;
    if (cursorType !== CursorType.GazeTracker) {
      return this.gazeCursorService.isUserPauseActive();
    }

    return this.gazeCursorService.toggleUserPause(this.gazePauseToggleElm);
  }

  isGazeCursorPaused(): boolean {
    return this.gazeCursorService.isUserPauseActive();
  }

  /**
   * Handles when the url changes.
   */
  private onUrlChanged(ev: any) {
    if (this.eyeTrackerNavStarted) {
      this.eyeTrackerNavService.placeCursor();
    }
    setTimeout(() => {
      this.setupNavigationCursor(ev.url);
    }, 500);
  }

  /**
   * Set up the way the nav cursor will pass through the navigable items.
   */
 private async setupNavigationCursor(pageUrl: string = '') {
    console.log('setupNavigationCursor');
    this.scanNavigationService.stopScan();
    const cursorType = this.profile.profile?.navSettingsBundle?.cursorType;
    const effectiveCursorType = cursorType ?? CursorType.GazeTracker;
    switch (effectiveCursorType) {
      case CursorType.Scan:
        if (this.eyeTrackerNavStarted) {
          this.eyeTrackerNavService.stop();
          this.eyeTrackerNavStarted = false;
        }
        // Stop gaze cursor if active
        this.gazeCursorService.stop();
        this.scanNavStarted = true;
        this.cursor.positionHV = 0;
        this.scanNavigationService.startScan(
          this.profile.navSettingsBundle.bundleOptionsState.scanInterval
        );
        break;
      case CursorType.EyeTracker:
        this.eyeTrackerNavStarted = true;
        this.eyeTrackerNavService.start();
        // Ensure gaze cursor is stopped when using classic eye tracker
        this.gazeCursorService.stop();
        return;
      case CursorType.GazeTracker:
        // New gaze-cursor: consume feedbackPositionH/V, manage dwell per element
        if (this.eyeTrackerNavStarted) {
          this.eyeTrackerNavService.stop();
          this.eyeTrackerNavStarted = false;
        }
        this.scanNavigationService.stopScan();
        this.removeNavCursorElm();
        this.gazeCursorService.refreshActivePageContext();
        await this.gazeCursorService.start();
        return;
      case CursorType.Touch:
        if (this.eyeTrackerNavStarted) {
          this.eyeTrackerNavService.stop();
          this.eyeTrackerNavStarted = false;
        }
        this.gazeCursorService.stop();
        return;
      default:
        if (this.eyeTrackerNavStarted) {
          this.eyeTrackerNavService.stop();
          this.eyeTrackerNavStarted = false;
        }
        this.gazeCursorService.stop();

        this.createNavCursorElm();
        this.initCursorPosition();
        break;
    }
    // } else {
    //   console.log(this.settings.navigationSource);
    //   switch (this.settings.navigationSource) {
    //     case NavigationSource.scan: {
    //       if(this.eyeTrackerNavStarted){ //se estiver usando peye e mudar de modo, desligar peye, mas se mantiver usando peye, mantém ele conectado
    //         this.eyeTrackerNavService.stop();
    //         this.eyeTrackerNavStarted = false;
    //       }
    //       this.scanNavStarted = true;
    //       this.cursor.positionHV = 0;
    //       this.scanNavigationService.startScan(this.settings.scanInterval);
    //       break;
    //     }
    //     case NavigationSource.eyeTracker: {
    //       this.eyeTrackerNavStarted = true;
    //       this.eyeTrackerNavService.start();
    //       return;
    //     }
    //     case NavigationSource.touch: {
    //       if(this.eyeTrackerNavStarted){
    //         this.eyeTrackerNavService.stop();
    //         this.eyeTrackerNavStarted = false;
    //       }
    //       return;
    //     }
    //     case NavigationSource.colibriEye: {
    //       this.eyeTrackerNavStarted = true;
    //       this.eyeTrackerNavService.start(this.settings.dwellDuration);
    //       return;
    //     }
    //     default: {
    //       if(this.eyeTrackerNavStarted){
    //         this.eyeTrackerNavService.stop();
    //         this.eyeTrackerNavStarted = false;
    //       }
    //       this.initCursorPosition();
    //     }
    //   }
    // }

    // Removes the cursor that was in the previous page.
    this.cursorElm.remove();

    // Appends the cursor at the same node of the first row. This way, the cursor can be
    // place behind the navigable items.
    const allRows = this.getNavigableRowsFromActivePage();
    if (allRows?.length > 0) {
      const rowElem = allRows.shift();

      rowElem?.append(this.cursorElm);
      rowElem?.append(this.feedbackElm);

      //console.log('rowElem REF: ', rowElem);

      // TODO: Começar com o cursor invisível até o primeiro movimento ou acionamento.

      // Start navigating by rows
      this.navigateRows();
    } else {
      // Inhibits the cursor if there are no navigable items on the page.
      this.cursorElm.remove();
      this.updateNavigableGroups([]);
    }
  }
  /**
   * Creates the navigation cursor element
   */
  private createNavCursorElm() {
    // console.log('[CURSOR] createNavCursorElm', this.user.appearanceCursorBorderColor);

    this.cursorElm.id = 'nav-cursor';

    switch (this.user.appearanceCursorBorderColor) {
      case 'cursor-border-color-yellow':
        this.cursorElm.style.cssText =
          'position:fixed;width:0;height:0;opacity:1;z-index:-1;background:rgb(247, 230, 0, 0.8);transition: all 500ms; border-radius: 10px; box-shadow: rgb(247, 230, 0) 0px 0px 5px 10px;';
        this.feedbackElm.style.cssText =
          'position:fixed; z-index:99; top: 0px; left: 0px; opacity: 1; background:rgba(247, 230, 0, 0.4); transition: width 500ms;';
        break;

      case 'cursor-border-color-red':
        this.cursorElm.style.cssText =
          'position:fixed;width:0;height:0;opacity:1;z-index:-1;background:rgb(255, 0, 0, 0.8);transition: all 500ms; border-radius: 10px; box-shadow: rgb(255, 0, 0) 0px 0px 5px 10px;';
        this.feedbackElm.style.cssText =
          'position:fixed; z-index:99; top: 0px; left: 0px; opacity: 1; background:rgba(255, 0, 0, 0.4); transition: width 500ms;';
        break;

      case 'cursor-border-color-green':
        this.cursorElm.style.cssText =
          'position:fixed;width:0;height:0;opacity:1;z-index:-1;background:rgb(0, 255, 0, 0.8);transition: all 500ms; border-radius: 10px; box-shadow: rgb(5, 179, 5) 0px 0px 5px 10px;';
        this.feedbackElm.style.cssText =
          'position:fixed; z-index:99; top: 0px; left: 0px; opacity: 1; background:rgba(0, 255, 0, 0.4); transition: width 500ms;';
        break;

      case 'cursor-border-color-black':
        this.cursorElm.style.cssText =
          'position:fixed;width:0;height:0;opacity:1;z-index:-1;background:rgb(0, 0, 0, 0.8);transition: all 500ms; border-radius: 10px; box-shadow: rgb(0, 0, 0) 0px 0px 5px 10px;';
        this.feedbackElm.style.cssText =
          'position:fixed; z-index:99; top: 0px; left: 0px; opacity: 1; background:rgba(0, 0, 0, 0.4); transition: width 500ms;';
        break;

      default: // Blue is the default
        // this.cursorElm.style.cssText =
        //   'position:fixed;width:0;height:0;opacity:1;z-index:-1;background:rgb(48, 107, 248, 0.8);transition: all 500ms; border-radius: 10px; box-shadow: rgb(48, 107, 248) 0px 0px 5px 10px;';
        this.feedbackElm.style.cssText =
          'position:fixed; z-index:99; top: 0px; left: 0px; opacity: 1; background:rgba(48, 108, 248, 0.4); transition: width 500ms;';
        break;
    }
  }

  /**
   * Removes the navigation cursor element
   */
  private removeNavCursorElm() {
    console.log('[CURSOR]  removeNavCursorElm');
    this.cursorElm?.remove();
    clearTimeout(this.highlightCursorItemsTimer);
    document.querySelectorAll('.app-nav-cursor-active').forEach((elm) => {
      elm.classList.remove('app-nav-cursor-active');
      elm.classList.remove('nav-cursor');
    });
  }

  /**
   * Initialize the cursor with the latest values provided by the mappings actionCursorH and actionCursorV
   */
  private initCursorPosition() {
    if (this.mappings.settings) {
      const indexH = this.mappings.settings.mappings.findIndex(
        (mapping) => mapping?.detectionAction?.function === 'actionCursorH'
      );
      const indexV = this.mappings.settings.mappings.findIndex(
        (mapping) => mapping?.detectionAction?.function === 'actionCursorV'
      );
      const indexHV = this.mappings.settings.mappings.findIndex(
        (mapping) => mapping?.detectionAction?.function === 'actionCursorHV'
      );

      this.cursor.positionH =
        indexH >= 0 ? this.mappings.state.mappings[indexH].output : 0;
      this.cursor.positionV =
        indexV >= 0 ? this.mappings.state.mappings[indexV].output : 0;
      this.cursor.positionHV =
        indexHV >= 0 ? this.mappings.state.mappings[indexHV].output : null;
    }
  }

  /**
   * Navigate the items that are in a row.
   */
  private navigateRows() {
    // Gets all navigable rows on the page
    const rows = this.getNavigableRowsFromActivePage();
    // this.navigableGroups = this.groupNavigableItemByResolution(rows);
    // this.placeCursorAt(this.navigableGroups[1]);

    this.updateNavigableGroups(rows);
  }

  /**
   * Handles when a navigable group is selected
   *
   * @param navGroup
   */
  private onNavigableGroupSelected(navGroup: Element[]) {
    // If the group is not defined, it is considered as empty.
    if (!navGroup) {
      navGroup = [];
    }
    console.log('onNavigableGroupSelected: ', navGroup, navGroup.length);
    console.log('INDEX: ', this.currentGroupIndex, this.currentElementIndex);
    // If the group has more than 1 item, it means that the cursor should go inside of the group's items.
    // In other words, the cursor will now be available on the selected group's items.
    if (navGroup.length > 1 && this.isLineSelectable(this.currentGroupIndex)) {
      this.updateNavigableGroups(navGroup);
      console.log('Group with more than 1 item selected');
    } else if (navGroup.length === 1) {
      // If the group has just one item, we check if it is a row by looking at the custom property called data-grid-row
      // that may be present on a navigable item.
      const navigableItem = navGroup[0];
      console.log('Single item selected: ', navigableItem);

      if ((navigableItem as any).dataset.hasOwnProperty('gridRow')) {
        // If the navigable item is a row, it's time to navigate through its inner items.
        // (The navigable items of a row must have the custom property called data-grid-col.)
        this.navigationStep++;
        const cols = Array.from(
          navigableItem.querySelectorAll('[data-grid-col]')
        );
        this.updateNavigableGroups(cols);

      } else if (this.isItemSelectable(this.currentGroupIndex, this.currentElementIndex)) {
        // The navigable item doen't have inner itens so it selects the item by calling the element`s click event
        this.navigationStep = 0;
        try {
          (navigableItem as any).click();
        } catch (err) {
          console.log(err);
        }

        // The proccess start from the beginning by navigating the rows again.
        this.navigateRows();
      }
    }
  }

  /**
   * Updates the navigable groups when the navigable items change.
   *
   * @param navigableItems new navigable items
   */
  private updateNavigableGroups(navigableItems: Element[]) {
    this.navigableGroups = this.groupNavigableItemByResolution(navigableItems);
    //console.log('updateNavigableGroups: ', this.navigableGroups);

    // Tenta descobrir a quantidade de linhas e colunas do grid para informar o serviço de
    // mapeamento sobre as resoluções V e H.
    if (this.navigableGroups.length) {
      if (this.cursor.positionHV !== null) {
        const resHV = this.navigableGroups.length;
        console.log('resHV => ', resHV);
        this.mappings.setResHV(resHV);
        this.scanNavigationService.setScanLength(resHV);
      } else {
        const resV = this.navigableGroups.length;
        let resH = 0;

        // Provisório: Considera a resolução H como sendo a maior possivel para o grid em questão.
        this.navigableGroups.forEach((group) => {
          const cols = Array.from(
            group[0].querySelectorAll('[data-grid-col]')
          ).length;
          if (cols > resH) {
            resH = cols;
          }
        });

        // console.log('resV =>', resV);
        // console.log('resH => ', resH);

        this.mappings.setResV(resV);
        this.mappings.setresH(resH);
      }
    }

    this.placeCursorAt(this.cursor);
  }

  /**
   * Groups the items taking into account the resolution. For instance, if there are 6 nav items
   * and the resolution is 3, we will have three groups of 2 items.
   *
   * @param navItems
   * @returns groups of navigable items
   */
  private groupNavigableItemByResolution(navItems: Element[]) {
    const groupSize = Math.ceil(
      navItems.length / this.resolutionHV
    );
    const groups = [];

    for (let i = 0; i < navItems.length; i += groupSize) {
      const group = navItems.slice(i, i + groupSize);
      groups.push(group);
    }
    return groups;
  }

  /**
   * Places the cursor at a given group that is comprised of items that can be
   * navigable.
   *
   * @param cursor
   */
  private placeCursorAt(cursor: Cursor) {
    const group = this.getNavigableGroupAt(cursor);

    clearTimeout(this.highlightCursorItemsTimer);
    document.querySelectorAll('.app-nav-cursor-active').forEach((elm) => {
      elm.classList.remove('app-nav-cursor-active');
    });

    if (group && group.length) {
      this.highlightCursorItemsTimer = setTimeout(() => {
        this.getGroupNavItems(group).forEach((navItem) =>
          navItem.classList.add('app-nav-cursor-active')
        );
      }, this.profile.navSettingsBundle.bundleOptionsState?.navGuardTimeout || 300);

      // Only emits the changes in the cursor postion for navigable items, not for rows or grups that
      // have more than one navigable item.
      if (group.length === 1) {
        this.navCursorMovedBS.next(group[0]);
      }

      // Gets the first and last item of the group
      const firstItem = group.slice(0)[0];
      const lastItem = group.slice(-1)[0];

      // Gets the rectangle that bounds the items.
      const firstItemRec = firstItem.getBoundingClientRect();
      const lastItemRec = lastItem.getBoundingClientRect();

      // Calculates the cursor's propreties like width and height so it envolves all items in the group.
      if (firstItemRec.top === lastItemRec.top) {
        // When the items are beside each other...
        const cursorWidth =
          lastItemRec.left - firstItemRec.left + lastItemRec.width;
        this.cursorElm.style.top = firstItemRec.top.toString() + 'px';
        this.cursorElm.style.left =
          (firstItemRec.left - this.cursorLeftOffset).toString() + 'px';
        this.cursorElm.style.width = cursorWidth.toString() + 'px';
        this.cursorElm.style.height = firstItemRec.height.toString() + 'px';
      } else {
        // When the items are beneath each other...
        const cursorHeight =
          lastItemRec.top - firstItemRec.top + lastItemRec.height;
        this.cursorElm.style.top = firstItemRec.top.toString() + 'px';
        this.cursorElm.style.left =
          (firstItemRec.left - this.cursorLeftOffset).toString() + 'px';
        this.cursorElm.style.width = firstItemRec.width.toString() + 'px';
        this.cursorElm.style.height = cursorHeight.toString() + 'px';
      }

      if ((firstItem as any).dataset.hasOwnProperty('gridRow')) {
        this.cursorElm.style.width = '100%';
        this.cursorElm.style.left = '0';
        this.cursorElm.style.borderRadius = '0';
        // this.cursorElm.style.boxShadow =
        //   'rgb(48, 107, 248, 0.8) 0px 0px 10px 12px'; //Condição eliminada para correção das bordas
      } else if ((firstItem as any).dataset.hasOwnProperty('gridCol')) {
        // Sets the cursor's border radius to match the item's border radius.
        this.cursorElm.style.borderRadius =
          window.getComputedStyle(firstItem).borderRadius;

        // this.cursorElm.style.boxShadow = 'rgbrgb(248, 48, 235)x 0px 5px 10px;';
      }
    }
  }

  private getNavigableGroupAt(cursor: Cursor) {
    let group = [];
    if (cursor.positionHV !== null) {
      if (cursor.positionHV < this.navigableGroups.length) {
        group = this.navigableGroups[cursor.positionHV];
      } else {
        console.error(
          'The cursor positionHV is outside the range of navigable groups.'
        );
        group = this.navigableGroups.slice(-1)[0];
      }
      this.currentGroupIndex = cursor.positionHV;
    } else if (this.navigableGroups.length > 0) {
      const positionV =
        cursor.positionV < this.navigableGroups.length
          ? cursor.positionV
          : this.navigableGroups.length - 1;

      try {
        const navigableItem = this.navigableGroups[positionV][0];

        if ((navigableItem as any).dataset.hasOwnProperty('gridRow')) {
          // If the navigable item is a row, it's time to navigate through its inner items.
          // (The navigable items of a row must have the custom property called data-grid-col.)
          const cols = Array.from(
            navigableItem.querySelectorAll('[data-grid-col]')
          );

          const positionH =
            cursor.positionH < cols.length ? cursor.positionH : cols.length - 1;

          group = [cols[positionH]];
        }
      } catch (err) { }

      this.currentGroupIndex = positionV;
      this.currentElementIndex = cursor.positionH;
    }

    return group;
  }

  private getGroupNavItems(group) {
    let navItems = [];

    group.forEach((item) => {
      if ((item as any).dataset.hasOwnProperty('gridRow')) {
        // If the navigable item is a row, it's time to navigate through its inner items.
        // (The navigable items of a row must have the custom property called data-grid-col.)
        const cols = Array.from(item.querySelectorAll('[data-grid-col]'));

        navItems = navItems.concat(cols);
      } else {
        navItems.push(item);
      }
    });
    return navItems;
  }

  private onSelectEvent() {
    this.onNavigableGroupSelected(this.getNavigableGroupAt(this.cursor));
  }

  private onCancelSelectEvent() {
    this.navigationStep = 0;
    this.navigateRows();
  }
  
  private async onDigitalEvent(data: MappingsOutput) {
    switch (data.type) {
      case MappingsOutputType.cursorPositionH:
        // console.log('cursorPositionH');
        this.cursor.positionH = data.value;
        this.cursor.positionHV = null;
        this.guardCursor(this.cursor);
        this.placeCursorAt(this.cursor);
        break;

      case MappingsOutputType.cursorPositionV:
        // console.log('cursorPositionV');
        this.cursor.positionV = data.value;
        this.cursor.positionHV = null;

        this.guardCursor(this.cursor);
        this.placeCursorAt(this.cursor);
        break;

      case MappingsOutputType.cursorPositionHV:
        // console.log('cursorPositionHV');
        this.cursor.positionHV = data.value;
        this.cursor.positionH = null;
        this.cursor.positionV = null;
        await this.updateHVResolution(data.resolution);
        this.guardCursor(this.cursor);
        this.placeCursorAt(this.cursor);
        break;

      // case MappingsOutputType.feedbackPositionH:
      //   //console.log('cursorPositionH', data.value);

      //   break;

      // case MappingsOutputType.cursorPositionV:
      //   break;

      // case MappingsOutputType.cursorPositionHV:
      //   break;
    }
  }

  async updateHVResolution(resolution: number) {
    if (resolution && (resolution !== this.resolutionHV)) {
      this.resolutionHV = resolution;
      await this.setupNavigationCursor();
    }
  }

  /**
   * Handles when scan navigation events
   *
   * @param ev
   */
  private onScanEvent(ev: ScanEvent) {
    //console.log('onScanEvent: ', ev);
    switch (ev.type) {
      case ScanEventType.indexChange: {
        // Maps the scan event as digital event of type cursor position HV.
        const digitalEvent = {
          type: MappingsOutputType.cursorPositionHV,
          value: ev.value,
          resolution: 6,
        };

        this.onDigitalEvent(digitalEvent);
        break;
      }
      case ScanEventType.cycleComplete: {
        //console.log('ScanEventType.cycleComplete');
        this.onCancelSelectEvent();
        break;
      }
    }
  }

  private filterEvents(data) {
    //console.log('[NavCursor] filterEvents', this.navEnabled, this.settings?.navigationSource);
    return this.navEnabled; // &&
    //this.settings?.navigationSource === NavigationSource.vision
  }

  private filterFeedbackEvents(data) {
    // console.log('[NavCursor] filterEvents', this.navEnabled && this.profile.navSettingsBundle?.bundleOptionsState?.selectRawFeedback);
    return (
      this.navEnabled &&
      this.profile.navSettingsBundle?.bundleOptionsState?.selectRawFeedback
      //this.settings?.navigationSource === NavigationSource.vision
    );
  }

  private guardCursor(cursor: Cursor) {
    this.restartNavGuardTimer();

    const deltaHV =
      cursor.positionHV && this.cursorFiltered
        ? Math.abs(cursor.positionHV - this.cursorFiltered.positionHV)
        : null;
    const deltaH =
      cursor.positionH && this.cursorFiltered
        ? Math.abs(cursor.positionH - this.cursorFiltered.positionH)
        : null;
    const deltaV =
      cursor.positionV && this.cursorFiltered
        ? Math.abs(cursor.positionV - this.cursorFiltered.positionV)
        : null;

    if (deltaHV > 1 || deltaH > 1 || deltaV > 1) {
      this.cursorFiltered = null;
    }

    clearTimeout(this.cursorFilterTimer);
    this.cursorFilterTimer = setTimeout(() => {
      this.cursorFiltered = JSON.parse(JSON.stringify(this.cursor));
      //console.log('CURSOR FILTERED: ', this.cursorFiltered, this.settings.cursorFilterTimeout || 300);
    }, this.profile.navSettingsBundle.bundleOptionsState?.cursorFilterTimeout || 300);
  }

  private guardSelectEvent(data) {
    if (this.isNavGuardTimerFinished()) {
      this.restartNavGuardTimer();
      return true;
    } else {
      if (this.cursorFiltered) {
        this.cursor = this.cursorFiltered;

        setTimeout(() => {
          this.cursorFiltered = null;
        });
        return true;
      }
    }
    return false;
  }

  private restartNavGuardTimer() {
    clearTimeout(this.navGuardTimer);
    this.navGuardTimerFinished = false;

    this.navGuardTimer = setTimeout(() => {
      this.onNavGuardTimerFinished();
    }, this.profile.navSettingsBundle.bundleOptionsState?.navGuardTimeout || 300);
  }

  private onNavGuardTimerFinished() {
    //console.log('onNavGuardTimerFinished');
    this.navGuardTimerFinished = true;
  }

  private isNavGuardTimerFinished() {
    return this.navGuardTimerFinished;
  }

  /**
   * Gets the navigable rows that are inside the current active ion page.
   */
  private getNavigableRowsFromActivePage() {
    const ionPages = Array.from(document.querySelectorAll('.ion-page'));

    let navRows: Array<Element> = [];
    if (ionPages.length > 0) {
      const lastIonPage = ionPages[ionPages.length - 1];
      const rows = lastIonPage.querySelectorAll('[data-grid-row]');
      navRows = Array.from(rows);
    } else {
      navRows = undefined;
    }
    //console.log('navRows: ', navRows)

    return navRows;
  }

  /**
   * Refreshes the dwell's progress bar.
   */
  private refreshFeedbackElm(value: number) {
    // Posterga a animação para evitar excesso de movimento na tela.
    if (value < 0.1) {
      value = 0;
      this.feedbackElm.style.transition = 'none';
    } else {
      // Rescales the progress beyond the 0.1 to 0-1 range.
      // Makes it reaches 1 when the value is 0.9.
      // Limits the value to 1.
      value = Math.min((value - 0.1) / 0.8, 1);
      this.feedbackElm.style.transition = 'width 200ms';
    }
    //console.log('refreshFeedbackElm', value);
    //this.feedbackElm.style.background = '#3880ff78';
    // this.dwellElm.width = this.dwellTimerProgress * 100;
    this.feedbackElm.style.top = this.cursorElm.offsetTop.toString() + 'px';
    this.feedbackElm.style.left = this.cursorElm.offsetLeft.toString() + 'px';
    this.feedbackElm.style.height =
      this.cursorElm.clientHeight.toString() + 'px';
    this.feedbackElm.style.width =
      (this.cursorElm.clientWidth * value).toString() + 'px';
  }

  setSelectableItems(selectable: [[number, number]]) {
    this.selectableItemsOnPage = selectable;
  }

  isLineSelectable(line: number): boolean {
    if (this.selectableItemsOnPage) {
      return this.selectableItemsOnPage.some(item => item[0] === line);
    } else { //se não houver definição de itens selecionáveis, todas as linhas são selecionáveis
      return true;
    }
  }

  isItemSelectable(line: number, column: number): boolean {
    if (this.selectableItemsOnPage) {
      return this.selectableItemsOnPage.some(item => item[0] === line && item[1] === column);
    } else { //se não houver definição de itens selecionáveis, todas as colunas são selecionáveis
      return true;
    }
  }

  setActivePage(page: NavigablePage, withoutBack: boolean = false) {
    console.log('setActivePage', page);
    this.activePage = page;
    this.activePageWithoutBack = withoutBack;
  }

  clearActivePage(page: NavigablePage) {
    if (this.activePage === page) {
      console.log('clearActivePage', page);
      this.activePage = null;
      this.selectableItemsOnPage = undefined;
    }
  }

  ngOnDestroy() {
    if (this.cancelSubscription) {
      this.cancelSubscription.unsubscribe();
    }
  }




}
